import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchPostsByPerson } from '../api/peoplePosts'
import type { PostCard } from '../types'

export function usePersonPosts(publicId?: string) {
  const [items, setItems] = useState<PostCard[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canLoadMore = useMemo(() => Boolean(cursor), [cursor])

  const load = useCallback(
    async (reset = false) => {
      if (!publicId || loading) return
      setLoading(true)
      setError(null)
      try {
        const res = await fetchPostsByPerson(publicId, reset ? null : cursor)
        setCursor(res.next_cursor)
        setItems((prev) => (reset ? res.items : [...prev, ...res.items]))
      } catch (e: any) {
        setError(e?.message || 'Failed to load posts')
      } finally {
        setLoading(false)
      }
    },
    [publicId, cursor, loading]
  )

  useEffect(() => {
    setItems([])
    setCursor(null)
    if (publicId) void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId])

  return { items, loading, error, canLoadMore, loadMore: () => load(false), refresh: () => load(true) }
}

